import { Avatar, Spinner2 } from 'components';
import { fmtDate } from '../constants';
import { ActionBadge, CategorySeverityBadge, ProfanityTriggerBadge } from './ui';

const COLS = ['Date', 'User', 'Content Type', 'Input', 'Action', 'Triggered', ''];

const UserCell = ({ user }) => {
  if (!user) return <span className="text-[12px] text-[#97A3B5]">System / deleted</span>;
  return (
    <div className="flex items-center gap-2">
      <Avatar user={user} avatar={user.avatar} tiny />
      <div className="flex flex-col min-w-0">
        <span className="text-[12px] text-[#353E4A] truncate max-w-[150px]">
          {user.name || user.username || '—'}
        </span>
        {(user.username || user.email) && (
          <span className="text-[11px] text-[#97A3B5] truncate max-w-[150px]">
            {user.username ? `@${user.username}` : user.email}
          </span>
        )}
      </div>
    </div>
  );
};

/** Every stored trigger: Azure categories at severity >= 2, plus the word-list hit. */
const TriggeredCell = ({ log }) => {
  const azureHits = (log.categories || []).filter((c) => c.severity >= 2);
  const hasProfanity = !!log.profanityLevel;

  if (!azureHits.length && !hasProfanity) {
    // Legacy rows may lack the categories breakdown — fall back to the single trigger.
    if (log.triggeredCategory) {
      return (
        <CategorySeverityBadge category={log.triggeredCategory} severity={log.triggeredSeverity} />
      );
    }
    return <span className="text-[12px] text-[#97A3B5]">—</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5 max-w-[260px]">
      {hasProfanity && <ProfanityTriggerBadge level={log.profanityLevel} />}
      {azureHits.map((c) => (
        <CategorySeverityBadge key={c.category} category={c.category} severity={c.severity} />
      ))}
    </div>
  );
};

const LogsTable = ({ rows, loading, onView }) => {
  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner2 />
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-12 h-12 rounded-xl bg-[#F0F2F4] flex items-center justify-center mb-4">
          <svg className="w-5 h-5 text-[#B5BDCA]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        </div>
        <p className="text-[13px] text-[#97A3B5]">No moderation logs match these filters</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#D2D8DF] overflow-x-auto">
      <table className="w-full text-left border-collapse min-w-[900px]">
        <thead>
          <tr className="bg-[#F0F2F4] border-b border-[#D2D8DF]">
            {COLS.map((col) => (
              <th
                key={col}
                className="px-3 py-2.5 text-[10px] font-semibold text-[#7989A0] uppercase tracking-wide whitespace-nowrap"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((log, i) => (
            <tr
              key={log._id}
              className={`border-b border-[#F0F2F4] last:border-0 hover:bg-[#F8FAFC] transition-colors ${
                i % 2 === 1 ? 'bg-[#FAFAFA]' : 'bg-white'
              }`}
            >
              <td className="px-3 py-2.5 text-[11px] text-[#5F6F86] whitespace-nowrap">
                {fmtDate(log.createdAt)}
              </td>
              <td className="px-3 py-2.5">
                <UserCell user={log.user} />
              </td>
              <td className="px-3 py-2.5 text-[12px] text-[#353E4A] whitespace-nowrap">
                {log.contentType}
              </td>
              <td className="px-3 py-2.5 whitespace-nowrap">
                <span className="text-[11px] font-medium text-[#5F6F86] bg-[#F0F2F4] rounded px-1.5 py-0.5 capitalize">
                  {log.inputType}
                </span>
              </td>
              <td className="px-3 py-2.5 whitespace-nowrap">
                <div className="flex items-center gap-1.5">
                  <ActionBadge action={log.action} />
                  {log.errored && (
                    <span
                      title={log.errorMessage || 'Safety service errored — fallback decision applied'}
                      className="text-[10px] font-semibold text-[#B45309] bg-[#FFF4E5] border border-[#FCE4C4] rounded-full px-1.5 py-0.5"
                    >
                      fallback
                    </span>
                  )}
                </div>
              </td>
              <td className="px-3 py-2.5">
                <TriggeredCell log={log} />
              </td>
              <td className="px-3 py-2.5 whitespace-nowrap text-right">
                <button
                  type="button"
                  onClick={() => onView(log)}
                  className="text-[12px] font-medium px-2.5 py-1 rounded-md border border-[#D2D8DF] text-[#5F6F86] hover:bg-[#F0F2F4] transition-colors"
                >
                  View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default LogsTable;
